/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module GslbModule */

import { IGslbSite } from 'generated-types';
import {
    LimitedListCollDataSource,
} from 'ajs/modules/data-model/factories/limited-list-coll-data-source.factory';
import { GSLBServiceFQDNCollection } from './gslb-service-fqdn.collection.factory';

interface IGslbServiceFqdnListRequestParamsResponse {
    offset_: number;
    limit_: number;
    results: IDomainInfo[];
}

interface IDomainInfo {
    domainName: string;
    siteName: string;
    gslbServiceName: string;
    vsId: string;
    clusterId: string;
}

/**
 * @description
 *
 *     getRequestParams actually builds the response from GSLB DNS VS sites and GSLBService domain
 *     names.
 *
 * @author Ram Pal
 */
export class GSLBServiceFQDNListCollDataSource extends LimitedListCollDataSource {
    public owner_: GSLBServiceFQDNCollection;

    /**
     * Making up the list of VS&FQDN combinations.
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public getRequestParams_(): IGslbServiceFqdnListRequestParamsResponse {
        const results: IDomainInfo[] = [];
        const { gslb, gslbService } = this.owner_;
        const gslbServiceDomainNames = gslbService.getDomainNames();
        const gslbServiceName = gslbService.getName();
        const sites = gslb.getConfig().sites.flattenConfig() as IGslbSite[];

        sites.forEach(site => {
            const {
                cluster_uuid: clusterId,
                dns_vses: dnsVses = [],
                name,
            } = site;

            dnsVses.forEach(dnsVs => {
                const {
                    dns_vs_uuid: vsId,
                    domain_names: subdomains = [],
                } = dnsVs;

                gslbServiceDomainNames.forEach(gslbServiceDomainName => {
                    const subdomainPresent = subdomains.some(subdomain => gslbServiceDomainName.endsWith(subdomain));

                    if (!subdomains.length || subdomainPresent) {
                        results.push({
                            domainName: gslbServiceDomainName,
                            clusterId,
                            siteName: name,
                            gslbServiceName,
                            vsId,
                        });
                    }
                });
            });
        });

        // need offset&limit for compatibility with ListCollDataSource.processResponse_
        return {
            offset_: 0,
            limit_: 0,
            results,
        };
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public processResponse_(resp: any, requestParams: any): void {
        this.processConfigResponse_(resp, requestParams);
    }
}
